import { IsNotEmpty, IsString } from 'class-validator';

/**
 * Response DTO for Vonage connection status
 */
export class VonageConnectionStatusDto {
	public connected!: boolean;
	public accountId?: string;
	public connectedDate?: string;
	public connectedByUserName?: string;
}

/**
 * Response DTO for Vonage authorization URL
 */
export class VonageAuthUrlDto {
	public url!: string;
	public state!: string;
}

/**
 * Request DTO for Vonage OAuth callback
 */
export class VonageCallbackDto {
	@IsString()
	@IsNotEmpty()
	public code!: string;

	@IsString()
	@IsNotEmpty()
	public state!: string;
}

/**
 * Internal model for Vonage authentication response
 */
export interface VonageAuthenticationResponseModel {
	token_type?: 'Bearer';
	access_token?: string;
	expires_in?: number;
	refresh_token?: string;
	refresh_token_expires_in?: number;
	scope?: string;
	account_id?: string;
	error?: string;
	error_description?: string;
}

/**
 * Response DTO for user dial status (click-to-dial capability)
 */
export class VonageUserDialStatusDto {
	public connected!: boolean;
	public canDial!: boolean;
	public userExtension!: string | null;
	public userExtensionName!: string | null;
}

/**
 * Request DTO for initiating a dial
 */
export class VonageDialRequestDto {
	@IsString()
	@IsNotEmpty()
	public phoneNumber!: string;
}

/**
 * Response DTO for dial result
 */
export class VonageDialResponseDto {
	public success!: boolean;
	public callId?: string;
	public error?: string;
	/** Whether the call was diverted to a different number */
	public diverted?: boolean;
	/** The phone number the call was diverted to (E.164 format) */
	public divertedTo?: string;
	/** The original phone number that was requested */
	public originalNumber?: string;
}

/**
 * Vonage extension user information from Provisioning API
 */
export interface VonageExtensionUserModel {
	id?: number;
	email?: string;
	login_name?: string;
	first_name?: string;
	last_name?: string;
}

/**
 * Vonage extension information from Provisioning API
 */
export interface VonageExtensionModel {
	extension_number: string;
	user?: VonageExtensionUserModel;
	location_id?: number;
	caller_id?: string;
	dids?: Array<{ phone_number?: string; custom_tag?: string }>;
	vtt_enabled?: boolean;
	dnd_enabled?: boolean;
	block_caller_id?: boolean;
	service_type_name?: string;
	external_id?: string;
}

/**
 * Response DTO for Vonage extensions list
 */
export class VonageExtensionDto {
	public extensionNumber!: string;
	public firstName!: string | null;
	public lastName!: string | null;
	public email!: string | null;
	public displayName!: string;
}

/**
 * Internal model for Vonage Provisioning API extensions response (HAL format)
 */
export interface VonageExtensionsResponseModel {
	page_size?: number;
	page?: number;
	total_pages?: number;
	total_items?: number;
	_embedded?: {
		extensions?: Array<VonageExtensionModel>;
	};
	_links?: {
		self?: { href?: string };
		first?: { href?: string };
		last?: { href?: string };
		next?: { href?: string };
		prev?: { href?: string };
	};
}

/**
 * Options for initiating a dial (used by client-side services)
 * Currently empty but added for future extensibility and API consistency
 */
export type VonageDialOptionsModel = Record<string, never>;

/**
 * Response DTO for user extension setup result
 * Used when user sets up their own extension via OAuth
 */
export class VonageUserExtensionResultDto {
	public success!: boolean;
	public extensionNumber?: string;
	public extensionName?: string;
	public error?: string;
}
